import { client, API_BASE_URL } from './api';

/**
 * Putting a PDF into an agency's fax inbox without a fax line.
 *
 * The inbox only fills when RingCentral delivers a webhook against a mapped
 * extension, which is true in production and nowhere else. This is how the
 * feature is exercised on every other stage. Refused outright in production,
 * and off unless the stage has enabled it.
 */

export interface DirectFaxBody {
  /** Left unset, the fax parks in the unassigned queue. */
  agency_id?: string | null;
  agency_clinic_id?: string | null;
  /** E.164. A reserved fiction number stands in when this is unset. */
  fax_number_from?: string | null;
  subject?: string | null;
  /**
   * The extension it should look as though it arrived on. Digits only.
   *
   * What makes a parked fax behave like a real one: the unassigned queue keys
   * on it, and so does the offer to collect a backlog when somebody maps that
   * extension.
   */
  ringcentral_owner_id?: string | null;
}

export interface DirectFax {
  id: string;
  agency_id: string | null;
  agency_clinic_id: string | null;
  /** Read out of the PDFs and summed, not sent by the caller. */
  page_count: number;
  fax_number_from: string;
  ringcentral_owner_id: string | null;
}

/**
 * Place a fax in an inbox by hand.
 *
 * Several files are one transmission carrying several attachments, in the
 * order given, which is what a real multi-document fax looks like. Sending
 * them one at a time would put several unrelated faxes in the inbox instead.
 */
export async function deliverDirectFax(
  fields: DirectFaxBody,
  files: File[]
): Promise<DirectFax> {
  const body = new FormData();
  // Omitted rather than sent empty: the endpoint reads an absent agency as
  // "park it", and an empty string would fail to parse as a UUID.
  for (const [key, value] of Object.entries(fields)) {
    if (value) body.append(key, value);
  }
  for (const file of files) body.append('files', file);

  // An explicit base URL and a raw FormData body: the request must keep the
  // multipart boundary axios generates, so no serializer is passed, and a
  // relative path can otherwise resolve against the frontend origin.
  const response = await client.request<DirectFax, unknown, true>({
    baseURL: API_BASE_URL,
    body,
    method: 'POST',
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/direct-faxes',
  });
  return response.data;
}
